import { useEffect, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import type { User, UserRole } from '../types/auth';
import { DemoBanner } from '../components/DemoBanner';
import { NoticeBanner } from '../components/NoticeBanner';
import { ThemeToggle } from '../components/ThemeToggle';
import { Logo } from '../components/brand/Logo';
import { PersonAvatar } from '../components/ui/PersonAvatar';
import { labelOf, t } from '../i18n';
import { dancerEmoji, type Honorific } from '../lib/honorific';
import { isDemoMode } from '../config/env';
import './AppShell.css';
import '../components/ui/uiAtoms.css';

const icons = {
  dashboard: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="3" width="8" height="8" />
      <rect x="13" y="3" width="8" height="5" />
      <rect x="13" y="10" width="8" height="11" />
      <rect x="3" y="13" width="8" height="8" />
    </svg>
  ),
  classes: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="8" cy="8" r="3" />
      <circle cx="16" cy="8" r="3" />
      <path d="M4 19c0-2.2 1.8-4 4-4h0c1.2 0 2.3.5 3 1.4M13 16.4c.7-.9 1.8-1.4 3-1.4h0c2.2 0 4 1.8 4 4" />
    </svg>
  ),
  schedule: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="3" y="5" width="18" height="16" />
      <path d="M3 10h18M8 3v4M16 3v4" />
    </svg>
  ),
  billing: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <rect x="2" y="6" width="20" height="13" />
      <path d="M2 10h20M7 15h4" />
    </svg>
  ),
  events: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M12 3v3M12 18v3M3 12h3M18 12h3" />
      <circle cx="12" cy="12" r="6" />
    </svg>
  ),
  notices: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 7h16v10H4z" />
      <path d="M8 7V5a4 4 0 0 1 8 0v2" />
    </svg>
  ),
  people: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="8" r="3" />
      <path d="M5 19c0-3 3-5 7-5s7 2 7 5" />
    </svg>
  ),
  profile: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="10" r="3" />
      <path d="M7 18c1.2-2 3-3 5-3s3.8 1 5 3" />
    </svg>
  ),
  analytics: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 19V9M10 19V5M16 19v-6M22 19V8" />
    </svg>
  ),
  settings: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H8a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V8c0 .7.4 1.3 1 1.5H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  ),
  leave: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M8 3v4M16 3v4" />
      <rect x="3" y="5" width="18" height="16" />
      <path d="M8 14h8M12 10v8" />
    </svg>
  ),
  attendance: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  activity: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  enrollments: (
    <svg className="app-shell__nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M22 11h-6" />
    </svg>
  ),
  logout: (
    <svg className="app-shell__logout-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  ),
};
type NavItem = {
  to: string;
  labelKey:
    | 'nav.dashboard'
    | 'nav.classes'
    | 'nav.mySchedule'
    | 'nav.events'
    | 'nav.billing'
    | 'nav.cashbook'
    | 'nav.students'
    | 'nav.people'
    | 'nav.enrollments'
    | 'nav.notices'
    | 'nav.profile'
    | 'nav.account'
    | 'nav.pay'
    | 'nav.analytics'
    | 'nav.leave'
    | 'nav.settings'
    | 'nav.attendance'
    | 'nav.activity';
  icon: ReactNode;
  groupKey: 'nav.group.overview' | 'nav.group.manage' | 'nav.group.finance' | 'nav.group.account';
};
function navForRole(role?: string): NavItem[] {
  if (role === 'operator') {
    return [
      { to: '/dashboard', labelKey: 'nav.dashboard', icon: icons.dashboard, groupKey: 'nav.group.overview' },
      { to: '/billing', labelKey: 'nav.cashbook', icon: icons.billing, groupKey: 'nav.group.finance' },
      { to: '/profile', labelKey: 'nav.profile', icon: icons.profile, groupKey: 'nav.group.account' },
    ];
  }
  if (role === 'coach') {
    return [
      { to: '/dashboard', labelKey: 'nav.dashboard', icon: icons.dashboard, groupKey: 'nav.group.overview' },
      { to: '/classes', labelKey: 'nav.mySchedule', icon: icons.schedule, groupKey: 'nav.group.overview' },
      { to: '/events', labelKey: 'nav.events', icon: icons.events, groupKey: 'nav.group.overview' },
      { to: '/billing', labelKey: 'nav.pay', icon: icons.billing, groupKey: 'nav.group.finance' },
      { to: '/profile', labelKey: 'nav.profile', icon: icons.profile, groupKey: 'nav.group.account' },
    ];
  }
  if (role === 'admin' || role === 'owner') {
    return [
      { to: '/dashboard', labelKey: 'nav.dashboard', icon: icons.dashboard, groupKey: 'nav.group.overview' },
      { to: '/activity', labelKey: 'nav.activity', icon: icons.activity, groupKey: 'nav.group.overview' },
      { to: '/analytics', labelKey: 'nav.analytics', icon: icons.analytics, groupKey: 'nav.group.overview' },
      { to: '/classes', labelKey: 'nav.classes', icon: icons.schedule, groupKey: 'nav.group.manage' },
      { to: '/enrollments', labelKey: 'nav.enrollments', icon: icons.enrollments, groupKey: 'nav.group.manage' },
      { to: '/events', labelKey: 'nav.events', icon: icons.events, groupKey: 'nav.group.manage' },
      { to: '/people', labelKey: 'nav.people', icon: icons.people, groupKey: 'nav.group.manage' },
      { to: '/leave', labelKey: 'nav.leave', icon: icons.leave, groupKey: 'nav.group.manage' },
      { to: '/notifications', labelKey: 'nav.notices', icon: icons.notices, groupKey: 'nav.group.manage' },
      { to: '/billing', labelKey: 'nav.billing', icon: icons.billing, groupKey: 'nav.group.finance' },
      { to: '/settings', labelKey: 'nav.settings', icon: icons.settings, groupKey: 'nav.group.account' },
      { to: '/profile', labelKey: 'nav.profile', icon: icons.profile, groupKey: 'nav.group.account' },
    ];
  }
  return [
    { to: '/dashboard', labelKey: 'nav.dashboard', icon: icons.dashboard, groupKey: 'nav.group.overview' },
    { to: '/classes', labelKey: 'nav.classes', icon: icons.schedule, groupKey: 'nav.group.overview' },
    { to: '/billing', labelKey: 'nav.billing', icon: icons.billing, groupKey: 'nav.group.finance' },
    { to: '/profile', labelKey: 'nav.account', icon: icons.profile, groupKey: 'nav.group.account' },
  ];
}
function mobileNavForRole(role: string | undefined, links: NavItem[]) {
  const byTo = (to: string) => links.find((item) => item.to === to);
  if (role === 'admin' || role === 'owner') {
    return [byTo('/dashboard'), byTo('/classes'), byTo('/billing'), byTo('/people'), byTo('/profile')].filter(
      (item): item is NavItem => Boolean(item),
    );
  }
  if (role === 'coach') {
    return [byTo('/dashboard'), byTo('/classes'), byTo('/billing'), byTo('/events'), byTo('/profile')].filter(
      (item): item is NavItem => Boolean(item),
    );
  }
  if (role === 'student') {
    return [byTo('/dashboard'), byTo('/classes'), byTo('/billing'), byTo('/profile')].filter(
      (item): item is NavItem => Boolean(item),
    );
  }
  if (role === 'operator') {
    return [byTo('/dashboard'), byTo('/billing'), byTo('/profile')].filter(
      (item): item is NavItem => Boolean(item),
    );
  }
  return links.slice(0, 5);
}
function navItemActive(to: string, pathname: string, isActive: boolean, role?: string) {
  if (to === '/classes') {
    return (
      pathname.startsWith('/classes') ||
      pathname.startsWith('/sessions') ||
      (role === 'student' && pathname.startsWith('/events'))
    );
  }
  if (to === '/profile' && role === 'student') {
    return pathname.startsWith('/profile') || pathname.startsWith('/leave') || pathname.startsWith('/notifications');
  }
  return isActive;
}
function groupNav(links: NavItem[]) {
  const order: NavItem['groupKey'][] = [
    'nav.group.overview',
    'nav.group.manage',
    'nav.group.finance',
    'nav.group.account',
  ];
  return order
    .map((groupKey) => ({
      groupKey,
      items: links.filter((item) => item.groupKey === groupKey),
    }))
    .filter((group) => group.items.length > 0);
}

function RoleSwitcher({
  user,
  currentRole,
  onSwitch,
}: {
  user: User | null;
  currentRole: UserRole;
  onSwitch: (role: UserRole) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const availableRoles = user?.roles && user.roles.length > 0 ? user.roles : [currentRole];

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  if (availableRoles.length <= 1) {
    return (
      <div className="app-shell__role-pill">
        <span aria-hidden="true">{roleEmoji(currentRole, user?.honorific)}</span>
        <span>{labelOf('role', currentRole)}</span>
      </div>
    );
  }

  return (
    <div className="app-shell__role-switcher-wrap" ref={containerRef}>
      <button
        type="button"
        className={`app-shell__role-pill app-shell__role-pill--button ${open ? 'app-shell__role-pill--open' : ''}`}
        onClick={() => setOpen(!open)}
        aria-haspopup="true"
        aria-expanded={open}
        title={t('role.switchPrompt')}
      >
        <span aria-hidden="true">{roleEmoji(currentRole, user?.honorific)}</span>
        <span>{labelOf('role', currentRole)}</span>
        <svg viewBox="0 0 20 20" fill="currentColor" className="app-shell__role-chevron" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div className="app-shell__role-dropdown" role="menu">
          <div className="app-shell__role-dropdown-header">{t('role.switchPrompt')}</div>
          {availableRoles.map((r) => {
            const isActive = r === currentRole;
            return (
              <button
                key={r}
                type="button"
                className={`app-shell__role-option ${isActive ? 'app-shell__role-option--active' : ''}`}
                onClick={() => {
                  setOpen(false);
                  if (!isActive) {
                    onSwitch(r);
                  }
                }}
                role="menuitem"
              >
                <div className="app-shell__role-option-content">
                  <span aria-hidden="true">{roleEmoji(r, user?.honorific)}</span>
                  <span>{labelOf('role', r)}</span>
                </div>
                {isActive && <span className="app-shell__role-check" aria-hidden="true">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function AppShell() {
  const { user, logout, switchRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const links = navForRole(user?.role);
  const groups = groupNav(links);
  const role = user?.role ?? 'student';
  const mobileLinks = mobileNavForRole(role, links);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleRoleSwitch = async (targetRole: UserRole) => {
    try {
      await switchRole(targetRole);
      if (isDemoMode) {
        window.location.hash = '#/dashboard';
        window.location.reload();
      } else {
        if (window.location.pathname === '/dashboard') {
          window.location.reload();
        } else {
          window.location.href = '/dashboard';
        }
      }
    } catch (err) {
      console.error('Failed to switch role', err);
    }
  };

  const handleLogout = () => {
    setMenuOpen(false);
    logout();
    navigate('/sign-in');
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const onChange = () => {
      if (media.matches) setMenuOpen(false);
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  return (
    <div className="app-shell" data-role={role}>
      <DemoBanner />
      <NoticeBanner />
      <div className="app-shell__body">
        <aside className="app-shell__sidebar" aria-label={t('nav.main')}>
          <div className="app-shell__brand">
            <div className="app-shell__brand-row">
              <Logo size="sm" showWordmark={false} />
              <div className="app-shell__brand-text">
                <p className="app-shell__brand-name">{t('brand')}</p>
                <p className="app-shell__brand-sub">{t('brand.institute')}</p>
              </div>
            </div>
            <RoleSwitcher user={user} currentRole={role} onSwitch={handleRoleSwitch} />
          </div>
          <nav className="app-shell__nav">
            {groups.map((group) => (
              <div key={group.groupKey} className="app-shell__nav-group">
                <p className="app-shell__nav-group-label">{t(group.groupKey)}</p>
                {group.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `app-shell__nav-link ${navItemActive(item.to, location.pathname, isActive, role) ? 'app-shell__nav-link--active' : ''}`
                    }
                  >
                    {item.icon}
                    {t(item.labelKey)}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>
          <div className="app-shell__sidebar-footer">
            <ThemeToggle />
            <div className="app-shell__user">
              <PersonAvatar
                name={user?.name ?? t('userFallback')}
                honorific={user?.honorific}
                photoFileId={user?.photoFileId}
                photoUrl={user?.photoUrl}
                userId={user?._id}
              />
              <div className="app-shell__user-info">
                <span className="app-shell__user-name">{user?.name ?? t('userFallback')}</span>
                <span className="app-shell__user-role">{labelOf('role', user?.role ?? 'student')}</span>
              </div>
            </div>
            <button type="button" className="app-shell__logout" onClick={handleLogout}>
              {icons.logout}
              <span>{t('nav.signOut')}</span>
            </button>
          </div>
        </aside>
        <div className="app-shell__main">
          <header className="app-shell__header">
            <div className="app-shell__brand-row">
              <Logo size="sm" showWordmark={false} />
              <p className="app-shell__brand-name">{t('brand')}</p>
              <RoleSwitcher user={user} currentRole={role} onSwitch={handleRoleSwitch} />
            </div>
            <button
              type="button"
              className="app-shell__menu-btn"
              aria-label={t('nav.openMenu')}
              aria-expanded={menuOpen}
              aria-controls="app-shell-drawer"
              onClick={() => setMenuOpen(true)}
            >
              <span className="app-shell__menu-bars" aria-hidden="true">
                <span />
                <span />
                <span />
              </span>
            </button>
          </header>
          <main className="app-shell__content">
            <Outlet />
          </main>
        </div>
      </div>
      <nav className="app-shell__bottom-nav" aria-label={t('nav.mobile')}>
        {mobileLinks.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `app-shell__bottom-link ${navItemActive(item.to, location.pathname, isActive, role) ? 'app-shell__bottom-link--active' : ''}`
            }
          >
            {item.icon}
            <span>{t(item.labelKey)}</span>
          </NavLink>
        ))}
      </nav>
      {menuOpen && (
        <div className="app-shell__drawer" id="app-shell-drawer" role="dialog" aria-modal="true" aria-label={t('nav.main')}>
          <button
            type="button"
            className="app-shell__drawer-backdrop"
            aria-label={t('nav.closeMenu')}
            onClick={() => setMenuOpen(false)}
          />
          <div className="app-shell__drawer-panel">
            <div className="app-shell__drawer-head">
              <div className="app-shell__user">
                <PersonAvatar
                  name={user?.name ?? t('userFallback')}
                  honorific={user?.honorific}
                  photoFileId={user?.photoFileId}
                  photoUrl={user?.photoUrl}
                  userId={user?._id}
                />
                <div className="app-shell__user-info">
                  <span className="app-shell__user-name">{user?.name ?? t('userFallback')}</span>
                  <span className="app-shell__user-role">{labelOf('role', role)}</span>
                </div>
              </div>
              <button type="button" className="app-shell__drawer-close" onClick={() => setMenuOpen(false)} aria-label={t('nav.closeMenu')}>
                ×
              </button>
            </div>
            <nav className="app-shell__nav app-shell__nav--drawer">
              {groups.map((group) => (
                <div key={group.groupKey} className="app-shell__nav-group">
                  <p className="app-shell__nav-group-label">{t(group.groupKey)}</p>
                  {group.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `app-shell__nav-link ${navItemActive(item.to, location.pathname, isActive, role) ? 'app-shell__nav-link--active' : ''}`
                      }
                    >
                      {item.icon}
                      {t(item.labelKey)}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <div className="app-shell__sidebar-footer">
              <ThemeToggle />
              <NavLink to="/profile" className="app-shell__drawer-profile" onClick={() => setMenuOpen(false)}>
                {icons.profile}
                {t('nav.profile')}
              </NavLink>
              <button type="button" className="app-shell__logout" onClick={handleLogout}>
                {icons.logout}
                <span>{t('nav.signOut')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function roleEmoji(role: string, honorific?: Honorific | null) {
  if (role === 'student' || role === 'coach') {
    return dancerEmoji(honorific);
  }
  if (role === 'owner') return '👑';
  if (role === 'admin') return '🏛️';
  if (role === 'operator') return '☕';
  return dancerEmoji(honorific);
}